import React, { useState } from "react";
import NewChatbotModal from "../../components/Dashboard/NewChatbotModal";
import roboImg from "../../assets/images/digital-robo.png";
import { useNavigate } from "react-router-dom";
import { Cpu, Globe, Zap, Beaker } from "lucide-react";

const ChatbotEmptyState = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <div className="bg-[#01040a] min-h-screen w-full px-6 md:px-16 font-sans text-white  relative overflow-hidden flex items-center py-20 lg:py-0">
        {/* --- STAINLESS CSS ANIMATIONS --- */}
        <style>{`
          @keyframes tunnel-travel {
            0% { transform: perspective(500px) rotateX(60deg) translateY(0); }
            100% { transform: perspective(500px) rotateX(60deg) translateY(60px); }
          }
          @keyframes energy-flow {
            0% { opacity: 0.1; transform: scale(0.8); }
            50% { opacity: 0.4; transform: scale(1.1); }
            100% { opacity: 0.1; transform: scale(0.8); }
          }
          @keyframes robo-float {
            0%, 100% { transform: translateY(0px) translateX(0px); }
            33% { transform: translateY(-15px) translateX(5px); }
            66% { transform: translateY(5px) translateX(-5px); }
          }
        `}</style>

        {/* 1. Perspective Tunnel Floor */}
        <div className="absolute bottom-0 left-0 w-full h-[60%] opacity-20 pointer-events-none mt-4">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(to right, #22d3ee 1px, transparent 1px), linear-gradient(to bottom, #22d3ee 1px, transparent 1px)`,
              backgroundSize: "50px 50px",
              animation: "tunnel-travel 1.5s linear infinite",
              maskImage: "linear-gradient(to top, black, transparent)",
            }}
          />
        </div>

        {/* 2. Cyber-Circuit Backdrop (Cross Lines Removed) */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          {/* Circular Orbital Ring remains for depth */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-cyan-500/30 rounded-full animate-[spin_60s_linear_infinite]" />
        </div>

        {/* 3. Deep Ambient Glows */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/10 blur-[150px] rounded-full animate-[energy-flow_10s_infinite]" />

        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-20">
          {/* LEFT SECTION (7 Columns) */}
          <div className="lg:col-span-7 flex flex-col z-30">
            {/* Animated Chip Icon */}
            <div className="mb-6 relative inline-block group">
              <div className="absolute inset-0 bg-cyan-400/20 blur-xl rounded-xl group-hover:bg-cyan-400/40 transition-all duration-700"></div>
              <div className="relative w-16 h-16 bg-[#0a121a] border border-cyan-400/30 rounded-xl flex items-center justify-center shadow-[0_0_30px_rgba(34,211,238,0.15)]">
                <div className="grid grid-cols-3 gap-1">
                  {[...Array(9)].map((_, i) => (
                    <div
                      key={i}
                      className="w-1.5 h-1.5 bg-cyan-400 rounded-[1px] animate-pulse shadow-[0_0_5px_#22d3ee]"
                      style={{ animationDelay: `${i * 150}ms` }}
                    />
                  ))}
                </div>
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold tracking-tight leading-tight mb-4 bg-gradient-to-b from-white via-white to-gray-400 bg-clip-text text-transparent">
              Empower your future with{" "}
              <span className="text-cyan-400 drop-shadow-[0_0_15px_rgba(34,211,238,0.5)]">
                MNR Digital Robo
              </span>
            </h1>

            <p className="text-slate-400 max-w-lg text-base md:text-lg leading-relaxed mb-8 font-light italic border-l-2 border-cyan-500/50 pl-4">
              I am freelancer available 24/7/365 days at your service. Train me
              how you want—I am your digital Robo ready to help.
            </p>

            {/* ACTION CARDS */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 mb-4">
              {/* Card 1 */}
              <div
                onClick={() => {
                  window.open(
                    "/webcontainer/index.html",
                    "_blank",
                    "noopener,noreferrer",
                  );
                }}
                className="group relative bg-[#0f172a]/80 backdrop-blur-md border border-white/10 p-5 rounded-2xl cursor-pointer transition-all duration-300 hover:border-cyan-400/40 hover:bg-[#1e293b]/50 hover:scale-[1.01] hover:shadow-[0_0_30px_rgba(34,211,238,0.1)] shadow-xl"
              >
                <div className="w-8 h-8 bg-cyan-400/10 rounded-full mb-3 flex items-center justify-center border border-white/10 group-hover:border-cyan-400/50 transition-all">
                  <Globe
                    size={18}
                    className="text-cyan-400 group-hover:scale-110 transition-transform"
                  />
                </div>
                <h3 className="text-md font-semibold mb-1 group-hover:text-cyan-300 transition-colors uppercase tracking-wider">
                  AI Web Developer
                </h3>
                <p className="text-slate-500 text-xs leading-snug">
                  Smart support for your website visitors.
                </p>
              </div>

              {/* Card 2 */}
              <div
                onClick={() => navigate("/dashboard/crm-ai")}
                className="group relative bg-[#0f172a]/80 backdrop-blur-md border border-white/10 p-5 rounded-2xl cursor-pointer transition-all duration-300 hover:border-cyan-400/40 hover:bg-[#1e293b]/50 hover:scale-[1.01] hover:shadow-[0_0_30px_rgba(34,211,238,0.1)] shadow-xl"
              >
                <div className="w-8 h-8 bg-cyan-400/10 rounded-full mb-3 flex items-center justify-center border border-white/10 group-hover:border-cyan-400/50 transition-all">
                  <Cpu
                    size={18}
                    className="text-cyan-400 group-hover:scale-110 transition-transform"
                  />
                </div>
                <h3 className="text-md font-semibold mb-1 group-hover:text-cyan-300 transition-colors uppercase tracking-wider">
                  AI CRM Agent
                </h3>
                <p className="text-slate-500 text-xs leading-snug">
                  Automate lead management and follow-ups.
                </p>
              </div>

              {/* Card 3 */}
              <div
                onClick={() => navigate("/dashboard/social-ai")}
                className="group relative bg-[#0f172a]/80 backdrop-blur-md border border-white/10 p-5 rounded-2xl cursor-pointer transition-all duration-300 hover:border-cyan-400/40 hover:bg-[#1e293b]/50 hover:scale-[1.01] hover:shadow-[0_0_30px_rgba(34,211,238,0.1)] shadow-xl"
              >
                <div className="w-8 h-8 bg-cyan-400/10 rounded-full mb-3 flex items-center justify-center border border-white/10 group-hover:border-cyan-400/50 transition-all">
                  <Zap
                    size={18}
                    className="text-cyan-400 group-hover:scale-110 transition-transform"
                  />
                </div>
                <h3 className="text-md font-semibold mb-1 group-hover:text-cyan-300 transition-colors uppercase tracking-wider">
                  AI Social Media Agent
                </h3>
                <p className="text-slate-500 text-xs leading-snug">
                  Engage audiences with AI-powered messaging across platforms.
                </p>
              </div>

              {/* Card 4 */}
              <div
                onClick={() => {
                  window.open("https://aitesting.mnrpureai.com", "_blank");
                }}
                className="group relative bg-[#0f172a]/80 backdrop-blur-md border border-white/10 p-5 rounded-2xl cursor-pointer transition-all duration-300 hover:border-cyan-400/40 hover:bg-[#1e293b]/50 hover:scale-[1.01] hover:shadow-[0_0_30px_rgba(34,211,238,0.1)] shadow-xl"
              >
                <div className="w-8 h-8 bg-cyan-400/10 rounded-full mb-3 flex items-center justify-center border border-white/10 group-hover:border-cyan-400/50 transition-all">
                  <Beaker
                    size={18}
                    className="text-cyan-400 group-hover:scale-110 transition-transform"
                  />
                </div>
                <h3 className="text-md font-semibold mb-1 group-hover:text-cyan-300 transition-colors uppercase tracking-wider">
                  AI Tester
                </h3>
                <p className="text-slate-500 text-xs leading-snug">
                  Execute end-to-end test suites and ensure code reliability.
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT SECTION: The Robo (5 Columns) */}
          <div className="lg:col-span-5 flex justify-center relative lg:mt-0 mt-16">
            <div className="absolute inset-0 bg-cyan-400/10 blur-[120px] rounded-full animate-pulse pointer-events-none" />

            <img
              src={roboImg}
              alt="AI Assistant"
              className="max-w-[320px] md:max-w-md w-full relative z-10 drop-shadow-[0_0_60px_rgba(34,211,238,0.4)] animate-[robo-float_8s_ease-in-out_infinite]"
            />

            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] border-dashed border-2 border-cyan-500/10 rounded-full animate-[spin_40s_linear_infinite] pointer-events-none" />
          </div>
        </div>
      </div>

      <NewChatbotModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreateChatbot={() => setIsModalOpen(false)}
      />
    </>
  );
};

export default ChatbotEmptyState;
